import { Component, OnInit, OnDestroy, inject, Injector, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, take } from 'rxjs/operators';
import { UserAuthenticatedComponent } from '../user-authenticated/user-authenticated.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SelectionService } from '../services/selection.service';
import { Store } from '@ngrx/store';
import { PolicyState } from '../store/policy/policy.reducer';
import { PolicyDetailsService } from '../services/policy-details.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { PolicySnapshotService } from '../services/data-service/policy-snapshot.service';
import { selectSelectedSection } from '../store/selection/selection.selector';
import * as PolicyActions from '../store/policy/policy.actions';
import { MatIconModule } from '@angular/material/icon';
import { ChatbotStateService } from '../shared/chatbot-state.service';
import { SECTION_SELECTOR_MAP } from '../shared/global-messages';
import { CardConfig } from './card-config.model';
import { CardOutletComponent } from './card-outlet/card-outlet.component';
import { CARD_CONFIGS } from './card-configs';

@Component({
    selector: 'app-profile-activities-view',
    imports: [CommonModule, MatIconModule, UserAuthenticatedComponent, CardOutletComponent],
    providers: [
        { provide: MatDialogRef, useValue: MatDialogRef },
        { provide: MAT_DIALOG_DATA, useValue: MAT_DIALOG_DATA },
    ],
    templateUrl: './profile-activities-view.component.html',
    styleUrl: './profile-activities-view.component.scss',
})
export class ProfileActivitiesViewComponent implements OnInit, OnDestroy {
    private fragmentSubscription?: Subscription;
    private loadingSubscription?: Subscription;
    private containerResizeObserver?: ResizeObserver;
    private resizeScrollTimeout?: any;
    private highlightClearTimeout?: any;
    private resizeListener = () => this.applyZoomFix();

    highlightedSection: string = '';
    activeCardName: string = '';
    isLandingPage: boolean = true;
    isThirdParty: boolean = false;
    isDCPolicyLoaded: boolean = false;
    isDCBillingLoaded: boolean = false;
    isCommLoaded: boolean = false;
    isDMSLoaded: boolean = false;
    isClaimsLoaded: boolean = false;
    dcPolicyFailed: boolean = false;
    dcBillingFailed: boolean = false;
    commFailed: boolean = false;
    dmsFailed: boolean = false;
    claimsFailed: boolean = false;
    isHandset$!: Observable<boolean>;
    fields: any[] = [];
    private combinedSubscription: Subscription;
    public policyDetails = inject(PolicyDetailsService);
    public policyDetailResponse: any;
    public injector = inject(Injector);
    private cdr = inject(ChangeDetectorRef);

    cardConfigs: CardConfig[] = CARD_CONFIGS;

    constructor(
        private breakpointObserver: BreakpointObserver,
        private policySnapshot: PolicySnapshotService,
        private route: ActivatedRoute,
        private selectionService: SelectionService,
        private router: Router,
        private store: Store<{ policy: PolicyState }>,
        private chatbotStateService: ChatbotStateService
    ) {
        this.isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
            map((result) => result.matches),
            shareReplay()
        );
    }
    selectedSection$ = this.store.select(selectSelectedSection);

    get visibleCards(): CardConfig[] {
        return this.cardConfigs.filter((card) => card.visibleWhen === 'always' || !this.isThirdParty);
    }

    getInputs(card: CardConfig): Record<string, any> {
        const inputs: Record<string, any> = {};
        for (const [inputName, propName] of Object.entries(card.inputs)) {
            inputs[inputName] = (this as any)[propName];
        }
        return inputs;
    }

    isCardHighlighted(card: CardConfig): boolean {
        return this.highlightedSection === card.highlightKey || this.activeCardName === card.name;
    }

    onCardClick(card: CardConfig) {
        this.activeCardName = this.activeCardName === card.name ? '' : card.name;
        this.highlightedSection = '';
    }

    onCardOutput(card: CardConfig, methodName: string) {
        if (methodName && typeof (this as any)[methodName] === 'function') {
            (this as any)[methodName]();
        }
    }

    get isLatestActivitiesLoaded(): boolean {
        return this.isDCPolicyLoaded || this.isDCBillingLoaded;
    }

    ngOnInit() {
        this.isThirdParty = this.router.url.includes('third-party');

        this.combinedSubscription = combineLatest([
            this.store.select((state) => state.policy.data).pipe(filter((data) => !!data)),
        ]).subscribe(([policyData]) => {
            let policyNumber = Object.keys(policyData)[0];
            const mapped = this.policySnapshot.mapApiToFieldsJson(policyData, policyNumber!);
            if (mapped && mapped.length) {
                this.fields = mapped;
            } else {
                console.warn('No mapped fields for policy:', policyNumber);
                this.fields = [];
            }
        });

        this.fragmentSubscription = this.route.fragment.subscribe((fragment) => {
            if (fragment) {
                setTimeout(() => this.scrollToSection(fragment), 100);
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParamsHandling: 'preserve',
                    fragment: undefined,
                    replaceUrl: true,
                });
            }
        });

        this.selectionService.isLandingPage$.pipe(distinctUntilChanged()).subscribe((value) => {
            this.isLandingPage = value;
        });

        this.loadingSubscription = combineLatest([
            this.store.select((state) => state.policy.termsLoading),
            this.store.select((state) => state.policy.dcPolicyLoading),
            this.store.select((state) => state.policy.dcBillingLoading),
            this.store.select((state) => state.policy.commLoading),
            this.store.select((state) => state.policy.dmsLoading),
            this.store.select((state) => state.policy.claimsLoading),
            this.store.select((state) => state.policy.data),
            this.store.select((state) => state.policy.policyNumber),
        ])
            .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe(([termsLoading, dcPolicy, dcBilling, comm, dms, claims, data, policyNumber]) => {
                this.isDCPolicyLoaded = (termsLoading || dcPolicy) as boolean;
                this.isDCBillingLoaded = (termsLoading || dcBilling) as boolean;
                this.isCommLoaded = (termsLoading || comm) as boolean;
                this.isDMSLoaded = (termsLoading || dms) as boolean;
                this.isClaimsLoaded = (termsLoading || claims) as boolean;

                const snapshot = (data as any)?.[policyNumber as string];
                const ps = snapshot?.policySnapshot;
                const isSectionFailed = (section: any) =>
                    Array.isArray(section) && section.length > 0 && section[0]?.status === 'failed';
                const hasSectionFailures = ps && (
                    isSectionFailed(ps.policyGeneralInformation) ||
                    isSectionFailed(ps.coverages) ||
                    isSectionFailed(ps.contactPreferences?.insuredInformation)
                );
                this.dcPolicyFailed = !dcPolicy && (!ps || hasSectionFailures);
                const bs = snapshot?.billingSnapshot;
                const hasBillingFailures = bs && isSectionFailed(bs.paymentInformation);
                this.dcBillingFailed = !dcBilling && (!bs || hasBillingFailures);
                this.commFailed = !comm && !snapshot?.inOutCommSnapshot;
                this.dmsFailed = !dms && !snapshot?.dmsSnapshot;
                this.claimsFailed = !claims && !snapshot?.claimsSnapshot;
            });

        this.applyZoomFix();
        window.addEventListener('resize', this.resizeListener);

        this.adjustContentForChatbot();
    }

    private adjustContentForChatbot() {
        setTimeout(() => {
            const chatbotEl = document.querySelector('app-chatbot-widget');
            if (chatbotEl) {
                this.chatbotStateService.applyCurrentWidthToDOM();
            } else {
                const container = document.querySelector('.profile-activities-container') as HTMLElement;
                if (container) {
                    container.style.right = '0px';
                }
            }
            this.observeContainerResize();
        });
    }

    private observeContainerResize() {
        const container = document.querySelector('.profile-activities-container') as HTMLElement;
        if (!container) return;
        this.containerResizeObserver?.disconnect();
        this.containerResizeObserver = new ResizeObserver(() => {
            this.rescrollToHighlighted();
        });
        this.containerResizeObserver.observe(container);
    }

    private rescrollToHighlighted() {
        if (!this.highlightedSection) return;
        clearTimeout(this.resizeScrollTimeout);
        this.resizeScrollTimeout = setTimeout(() => {
            this.scrollToSection(this.highlightedSection);
        }, 200);
    }

    applyZoomFix() {
        const zoomLevel = window.outerWidth / window.innerWidth;
        const parentClass = document.querySelector('.parent-class') as HTMLElement;
        const sidebar = document.querySelector('.hamburgerMenu') as HTMLElement;

        if (parentClass) {
            const sidebarOpen = sidebar?.classList.contains('active');
            parentClass.style.left = sidebarOpen ? `${356 * zoomLevel}px` : '0px';
            parentClass.style.right = `${356 * zoomLevel}px`;
        }
    }

    ngOnDestroy() {
        if (this.combinedSubscription) {
            this.combinedSubscription.unsubscribe();
        }
        this.fragmentSubscription?.unsubscribe();
        this.loadingSubscription?.unsubscribe();
        this.containerResizeObserver?.disconnect();
        clearTimeout(this.highlightClearTimeout);
        window.removeEventListener('resize', this.resizeListener);
    }

    private scrollToSection(sectionId: string) {
        this.highlightedSection = sectionId;
        this.activeCardName = '';
        this.cdr.markForCheck();

        clearTimeout(this.highlightClearTimeout);
        this.highlightClearTimeout = setTimeout(() => {
            this.highlightedSection = '';
            this.cdr.markForCheck();
        }, 3000);

        const selector = SECTION_SELECTOR_MAP[sectionId];
        const element = selector
            ? (document.querySelector(selector) as HTMLElement)
            : document.getElementById(sectionId);
        if (element) {
            const container = document.querySelector('.profile-activities-container') as HTMLElement;
            if (container) {
                const stickyEl = container.querySelector('app-policy-number') as HTMLElement;
                const stickyHeight = stickyEl ? stickyEl.offsetHeight : 0;
                const gap = 32;
                const top = element.offsetTop - container.offsetTop - stickyHeight + gap;
                container.scrollTo({ top, behavior: 'smooth' });
            }
        }
    }

    retryDCPolicy() {
        this.store
            .select((state) => state.policy)
            .pipe(take(1))
            .subscribe((policy) => {
                const policyNum = policy.policyNumber!;
                const generalInfo = policy.data?.[policyNum]?.policySnapshot?.policyGeneralInformation;
                this.store.dispatch(
                    PolicyActions.retryDCPolicy({
                        policyNumber: policyNum,
                        policyTerm: policy.policyTerm || undefined,
                        effectiveDate: generalInfo?.effectiveDate?.value ?? undefined,
                        expireDate: generalInfo?.expirationDate?.value ?? undefined,
                    })
                );
            });
    }

    retryDCBilling() {
        this.store
            .select((state) => state.policy)
            .pipe(take(1))
            .subscribe((policy) => {
                this.store.dispatch(
                    PolicyActions.retryDCBilling({
                        policyNumber: policy.policyNumber!,
                        policyTerm: policy.policyTerm || undefined,
                    })
                );
            });
    }

    retryComm() {
        this.store
            .select((state) => state.policy)
            .pipe(take(1))
            .subscribe((policy) => {
                this.store.dispatch(
                    PolicyActions.retryComm({
                        policyNumber: policy.policyNumber!,
                        policyTerm: policy.policyTerm || undefined,
                    })
                );
            });
    }

    retryDMS() {
        this.store
            .select((state) => state.policy)
            .pipe(take(1))
            .subscribe((policy) => {
                this.store.dispatch(
                    PolicyActions.retryDMS({
                        policyNumber: policy.policyNumber!,
                        policyTerm: policy.policyTerm || undefined,
                    })
                );
            });
    }

    retryClaims() {
        this.store
            .select((state) => state.policy)
            .pipe(take(1))
            .subscribe((policy) => {
                this.store.dispatch(
                    PolicyActions.retryClaims({
                        policyNumber: policy.policyNumber!,
                        policyTerm: policy.policyTerm || undefined,
                    })
                );
            });
    }

    retryLatestActivities() {
        this.store
            .select((state) => state.policy)
            .pipe(take(1))
            .subscribe((policy) => {
                const policyNum = policy.policyNumber!;
                const generalInfo = policy.data?.[policyNum]?.policySnapshot?.policyGeneralInformation;
                this.store.dispatch(PolicyActions.retryDCPolicy({
                    policyNumber: policyNum,
                    policyTerm: policy.policyTerm || undefined,
                    effectiveDate: generalInfo?.effectiveDate?.value ?? undefined,
                    expireDate: generalInfo?.expirationDate?.value ?? undefined,
                }));
                this.store.dispatch(PolicyActions.retryDCBilling({ policyNumber: policyNum, policyTerm: policy.policyTerm || undefined }));
            });
    }
}
